import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { syncServerClock } from '../lib/clock'
import { log, initLogLevel } from '../lib/logger'
import { normalizeHole } from '../lib/holes'
import { apiError } from '../lib/api'

const WSContext = createContext(null)
export const useWS = () => useContext(WSContext)

export const MAX_FEED = 120
const MAX_HOLES = 200

// latestTs: o mais recente de dois instantes ISO (qualquer um pode faltar).
function latestTs(a, b) {
  if (!a) return b ?? a ?? null
  if (!b) return a
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b
}

export function WSProvider({ children }) {
  const [connected, setConnected] = useState(false)
  const [devices, setDevices] = useState([])
  const [pendingDevices, setPendingDevices] = useState([])
  const [feed, setFeed] = useState([])
  const [categories, setCategories] = useState([])
  const [mines, setMines] = useState([])
  const [operators, setOperators] = useState([])
  const [clients, setClients] = useState([])
  const [checklists, setChecklists] = useState([])
  const [models, setModels] = useState([])
  const [alarms, setAlarms] = useState([])
  const [maps, setMaps] = useState([])
  const [holes, setHoles] = useState([]) // furos concluídos ao vivo (hole_done), newest-first
  const [updateStatuses, setUpdateStatuses] = useState({}) // device -> histórico de update_status (OTA)
  const [feedPaused, setFeedPausedState] = useState(false)
  // Marcador de ciclo concluído: sobe a cada frame cycle_done (e no clear). A tela de
  // Ciclos escuta isto, não o feed: o feed pode estar pausado no servidor e aí ela
  // congelava para sempre, sem aviso.
  const [cycleTick, setCycleTick] = useState(0)
  const [feedDropped, setFeedDropped] = useState(0) // eventos que caíram do teto do feed
  const [adminError, setAdminError] = useState(null) // falha ao carregar os cadastros
  const wsRef = useRef(null)
  const genRef = useRef(0)
  const attemptRef = useRef(0)
  const reconnectRef = useRef(null)
  const feedPausedRef = useRef(false)
  const feedSeqRef = useRef(0)

  // Pausa/retoma o fluxo de eventos NO SERVIDOR (economia de banda): envia um
  // frame de controle; o backend para de mandar mensagens tipo "event" a este
  // cliente. Os snapshots (devices/pending/alarmes/mapas) continuam.
  const setFeedPaused = useCallback((paused) => {
    feedPausedRef.current = paused
    setFeedPausedState(paused)
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'feed_pause', paused }))
    }
  }, [])

  // provisioned e failed ficam visíveis; provisioned vira "aguardando mensagens" e
  // só sai quando o device manda a primeira telemetria (efeito abaixo). delete remove.
  const upsertPending = useCallback((p) => {
    setPendingDevices(prev => {
      const idx = prev.findIndex(x => x.id === p.id)
      if (idx === -1) return [p, ...prev]
      const next = [...prev]
      next[idx] = p
      return next
    })
  }, [])

  // O handshake do WebSocket não é coberto por CORS e o navegador manda os cookies
  // nele, então autenticar só pelo cookie seria sequestro de socket entre sites (o
  // atacante LÊ as respostas, porque o canal é bidirecional). O socket exige um ticket
  // de uso único, pedido antes numa chamada da própria origem que já passou por sessão
  // e CSRF.
  const connect = useCallback(async () => {
    // Contador de geração: o `connect` virou assíncrono (precisa buscar o ticket antes),
    // e nesse intervalo `wsRef.current` ainda é nulo. Sem esta guarda, uma limpeza do
    // efeito durante a espera não cancela nada e o socket criado depois fica órfão, com
    // os handlers vivos. Em dev o StrictMode reproduz isso a cada montagem: dois sockets,
    // cada evento contado em dobro.
    const gen = ++genRef.current
    let ticket = ''
    try {
      const res = await fetch('/api/ws/ticket', { method: 'POST' })
      if (res.status === 401) return // sem sessão: o gate do App já vai tratar
      if (res.ok) ticket = (await res.json()).ticket || ''
    } catch { /* rede fora: tenta conectar assim mesmo e cai no retry */ }

    if (gen !== genRef.current) return // desmontou durante a busca do ticket

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const qs = ticket ? `?ticket=${encodeURIComponent(ticket)}` : ''
    const ws = new WebSocket(`${proto}://${window.location.host}/ws${qs}`)
    if (gen !== genRef.current) { ws.close(); return }
    wsRef.current = ws

    ws.onopen = () => {
      setConnected(true)
      log.info('ws conectado')
      attemptRef.current = 0
      if (reconnectRef.current) clearTimeout(reconnectRef.current)
      // Reaplica o estado de pausa após (re)conexão.
      if (feedPausedRef.current) {
        ws.send(JSON.stringify({ type: 'feed_pause', paused: true }))
      }
    }

    ws.onclose = async (ev) => {
      if (gen !== genRef.current) return
      setConnected(false)
      // Fechamento limpo (logout, navegação): não reconecta nem gasta uma chamada.
      if (ev && ev.code === 1000) return
      // Antes de reagendar, confere se a sessão ainda vale. Sem isto, uma sessão
      // expirada vira um laço infinito de reconexão, porque o onclose antigo nunca
      // olhava o motivo do fechamento.
      try {
        const me = await fetch('/api/auth/me')
        if (me.status === 401) {
          log.warn('ws fechado: sessão expirada')
          return
        }
      } catch { /* rede fora: reconecta com backoff */ }
      if (gen !== genRef.current) return
      // Backoff exponencial com jitter, teto de 30s. O intervalo fixo de 3s custava
      // três requisições por volta (ticket + handshake + /me) para sempre quando o
      // socket estava inalcançável mas o backend de pé.
      const n = attemptRef.current++
      const delay = Math.min(30000, 1000 * 2 ** n) * (0.5 + Math.random())
      log.warn(`ws desconectado, reconectando em ${Math.round(delay)}ms`)
      reconnectRef.current = setTimeout(connect, delay)
    }

    ws.onerror = (e) => { log.error('ws erro', e); ws.close() }

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data)
        log.debug('ws recv', msg.type, msg.payload)
        // Todo frame carrega o relógio do servidor: é com ele que a interface decide
        // o estado de cada equipamento, não com o da estação do operador.
        syncServerClock(msg.server_time)
        if (msg.type === 'devices') {
          // O snapshot do banco não carrega o timestamp do fix; preserva o já conhecido,
          // senão o animador do mapa veria a mesma posição com "hora" nova a cada 10 s.
          // O sinal de vida e a versão do protocolo também chegam pelos eventos entre
          // dois snapshots: vale o mais recente, e um backend que ainda não os manda
          // não apaga o que o evento trouxe.
          setDevices(prev => {
            const known = new Map(prev.map(d => [d.name, d]))
            return (msg.payload || []).map(d => {
              const old = known.get(d.name)
              if (!old) return d
              const next = { ...d }
              if (old.lastFixTs != null) next.lastFixTs = old.lastFixTs
              next.lastHealthAt = latestTs(d.lastHealthAt, old.lastHealthAt)
              if (d.vmcrpcVersion == null && old.vmcrpcVersion != null) next.vmcrpcVersion = old.vmcrpcVersion
              return next
            })
          })
        } else if (msg.type === 'pending_devices') {
          setPendingDevices(msg.payload || [])
        } else if (msg.type === 'pending_device' || msg.type === 'pending_device_updated') {
          upsertPending(msg.payload)
        } else if (msg.type === 'pending_device_deleted') {
          setPendingDevices(prev => prev.filter(p => p.id !== msg.payload.id))
        } else if (msg.type === 'device_deleted') {
          setDevices(prev => prev.filter(d => d.name !== msg.payload.name))
        } else if (msg.type === 'device_events_cleared') {
          setDevices(prev => prev.map(d =>
            d.name === msg.payload.name ? { ...d, eventCount: 0 } : d
          ))
        } else if (msg.type === 'event') {
          setDevices(prev => {
            const p = msg.payload
            const exists = prev.find(d => d.name === p.deviceName)
            if (!exists) return prev
            const hasTelem = p.telemetry != null
            // "Mensagem do device" (atualiza última comunicação): telemetria, evento VMC
            // ou resposta a ping/health. Comandos de escrita (reset/cleanCache) NÃO contam
            // senão o last_seen oscilaria contra o snapshot do banco.
            const deviceSpoke = ('telemetry' in p)
              || p.commandType === 'vmc_event'
              || (p.commandType === 'command' && p.ok && (p.command === 'ping' || p.command === 'health'))
            const healthAt = p.lastHealthAt !== undefined ? p.lastHealthAt
              : (p.commandType === 'vmc_event' && (p.method === 'health' || p.method === 'heartbeat')) ? p.receivedAt
              : undefined
            return prev.map(d =>
              d.name !== p.deviceName ? d : {
                ...d,
                lastSeen:        deviceSpoke ? p.receivedAt : d.lastSeen,
                lastTelemetryAt: hasTelem ? p.receivedAt : d.lastTelemetryAt,
                operatorName:    p.operatorName !== undefined ? p.operatorName : d.operatorName,
                cycleState:      p.cycleState   !== undefined ? p.cycleState   : d.cycleState,
                cycleSince:      p.cycleSince   !== undefined ? p.cycleSince   : d.cycleSince,
                stopBucket:      p.stopBucket   !== undefined ? p.stopBucket   : d.stopBucket,
                stopReason:      p.stopReason   !== undefined ? p.stopReason   : d.stopReason,
                lastFuelPct:     p.fuelPct      !== undefined ? p.fuelPct      : d.lastFuelPct,
                lastFuelAt:      p.fuelAt       !== undefined ? p.fuelAt       : d.lastFuelAt,
                lastHorimeterH:  p.horimeterH   !== undefined ? p.horimeterH   : d.lastHorimeterH,
                lastHorimeterAt: p.horimeterAt  !== undefined ? p.horimeterAt  : d.lastHorimeterAt,
                lastHealthAt:    healthAt       !== undefined ? latestTs(healthAt, d.lastHealthAt) : d.lastHealthAt,
                vmcrpcVersion:   p.vmcrpcVersion ? p.vmcrpcVersion : d.vmcrpcVersion,
                lastLat:  p.telemetry?.latitude  ?? d.lastLat,
                lastLon:  p.telemetry?.longitude ?? d.lastLon,
                lastFixTs: (hasTelem && p.deviceTs) ? p.deviceTs : d.lastFixTs,
                lastAltM:       p.telemetry?.altitude_m  ?? d.lastAltM,
                lastSpeedKmh:   p.telemetry?.speed_kmh   ?? d.lastSpeedKmh,
                lastHeadingDeg: p.telemetry?.heading_deg ?? d.lastHeadingDeg,
                eventCount: d.eventCount + 1,
                online: deviceSpoke ? true : d.online,
              }
            )
          })
          if (msg.payload.commandType !== 'session') {  // 'session' é só estado de login, não vai pro feed
            // feedKey: identidade estável de cada linha. Os comandos chegam sem id e a
            // lista é reordenada a cada frame, então uma chave por índice trocava de
            // linha e o RAW aberto fechava sozinho ou mostrava outro payload.
            feedSeqRef.current += 1
            const item = { ...msg.payload, feedKey: `f${feedSeqRef.current}` }
            setFeed(prev => {
              const next = [item, ...prev]
              if (next.length <= MAX_FEED) return next
              setFeedDropped(n => n + (next.length - MAX_FEED))
              return next.slice(0, MAX_FEED)
            })
          }
        } else if (msg.type === 'cycle_done') {
          setCycleTick(n => n + 1)
        } else if (msg.type === 'geofence_alarms') {
          setAlarms(msg.payload || [])
        } else if (msg.type === 'geofence_alarm') {
          setAlarms(prev => [msg.payload, ...prev].slice(0, 300))
        } else if (msg.type === 'geofence_alarm_ack') {
          setAlarms(prev => prev.map(a =>
            (msg.payload.all || a.id === msg.payload.id) ? { ...a, acknowledged: true } : a
          ))
        } else if (msg.type === 'maps') {
          setMaps(msg.payload || [])
        } else if (msg.type === 'map_status') {
          setMaps(prev => {
            const idx = prev.findIndex(m => m.id === msg.payload.id)
            if (idx === -1) return [msg.payload, ...prev]
            const next = [...prev]
            next[idx] = msg.payload
            return next
          })
        } else if (msg.type === 'map_deleted') {
          setMaps(prev => prev.filter(m => m.id !== msg.payload.id))
        } else if (msg.type === 'hole_done') {
          const hole = normalizeHole(msg.payload)
          if (hole && hole.id != null) {
            // A outbox do VMC pode reentregar o mesmo furo; dedupe pelo id da row. Uma
            // reentrega com status diferente (concluído x descartado) sobe ao topo com o
            // status novo, que é o que a tela Furos observa para refazer a busca.
            setHoles(prev => {
              const old = prev.find(h => h.id === hole.id)
              if (old && old.status === hole.status) return prev
              return [hole, ...prev.filter(h => h.id !== hole.id)].slice(0, MAX_HOLES)
            })
          }
        } else if (msg.type === 'update_status') {
          setUpdateStatuses(prev => {
            const list = prev[msg.payload.device] || []
            // O backend emite um "aceito" sintético e o device emite o dele;
            // entradas idênticas em janela curta viram uma só na timeline.
            const last = list[list.length - 1]
            if (last && last.action === msg.payload.action && last.status === msg.payload.status &&
                (last.packages || []).join(',') === (msg.payload.packages || []).join(',') &&
                Math.abs((msg.payload.ts || 0) - (last.ts || 0)) < 5000) {
              return prev
            }
            return { ...prev, [msg.payload.device]: [...list, msg.payload].slice(-50) }
          })
        }
      } catch (_) {}
    }
  }, [upsertPending])

  useEffect(() => { initLogLevel() }, [])

  useEffect(() => {
    connect()
    return () => {
      // Invalida a geração ANTES de fechar: é o que faz um `connect` ainda parado no
      // await do ticket desistir em vez de criar um socket órfão.
      genRef.current++
      if (reconnectRef.current) clearTimeout(reconnectRef.current)
      wsRef.current?.close()
    }
  }, [connect])

  // remove o pending "provisionado, aguardando mensagens" quando o device manda a primeira telemetria
  useEffect(() => {
    setPendingDevices(prev => {
      const next = prev.filter(p => {
        if (p.status !== 'provisioned') return true
        const dev = devices.find(d => d.name === p.vmc_name)
        return !(dev && dev.lastTelemetryAt)
      })
      return next.length === prev.length ? prev : next
    })
  }, [devices])

  // Cadastros (Frota/Minas) compartilhados: recarregados após editar para os cards
  // refletirem as mudanças ao vivo. A falha de carga é guardada em adminError: sem
  // isso, backend fora do ar e cadastro vazio eram a mesma tela ("cadastre o primeiro").
  const reloadAdmin = useCallback(() => {
    const get = (url, set) => fetch(url)
      .then(r => { if (!r.ok) throw new Error(`${url}: HTTP ${r.status}`); return r.json() })
      .then(d => { set(d || []); return null })
      .catch(err => err.message)
    Promise.all([
      get('/api/equipment-categories', setCategories),
      get('/api/mines', setMines),
      get('/api/operators', setOperators),
      get('/api/clients', setClients),
      get('/api/checklists', setChecklists),
      get('/api/equipment-models', setModels),
    ]).then(errs => setAdminError(errs.find(Boolean) || null))
  }, [])
  useEffect(() => { reloadAdmin() }, [reloadAdmin])

  const ackAlarm = useCallback((id) => {
    setAlarms(prev => prev.map(a => a.id === id ? { ...a, acknowledged: true } : a))
    fetch(`/api/geofence-alarms/${id}/ack`, { method: 'POST' }).catch(() => {})
  }, [])
  const ackAllAlarms = useCallback(() => {
    setAlarms(prev => prev.map(a => ({ ...a, acknowledged: true })))
    fetch('/api/geofence-alarms/ack-all', { method: 'POST' }).catch(() => {})
  }, [])
  // Apaga no servidor primeiro: um 500 tem que voltar ao chamador (o diálogo mostra),
  // em vez de esvaziar a tela e fingir que apagou.
  // O escopo de mina vai ao servidor: sem ele o DELETE apagava o histórico de todas as
  // minas. A lista volta do próprio servidor (o que sobrou), não é esvaziada aqui.
  const clearAlarms = useCallback(async (mineQS = '') => {
    const res = await fetch(`/api/geofence-alarms${mineQS ? `?${mineQS}` : ''}`, { method: 'DELETE' })
    if (!res.ok) throw await apiError(res)
  }, [])

  const resetFeedWindow = useCallback(() => setFeedDropped(0), [])

  const reloadMaps = useCallback(() => {
    fetch('/api/maps').then(r => r.json()).then(d => setMaps(d || [])).catch(() => {})
  }, [])

  return (
    <WSContext.Provider value={{ connected, devices, pendingDevices, feed, feedDropped, resetFeedWindow, cycleTick, categories, mines, operators, clients, checklists, models, alarms, maps, holes, updateStatuses, adminError, feedPaused, setFeedPaused, ackAlarm, ackAllAlarms, clearAlarms, reloadAdmin, reloadMaps }}>
      {children}
    </WSContext.Provider>
  )
}
